// footer.tsx
import { Flex, Text } from "@radix-ui/themes";
import styles from "./footer.module.css";
import { useMemo } from "react";
import { Link } from "~/components/RouterLink";
import { Instagram } from "~/components/icons/Instagram";
import { Facebook } from "~/components/icons/Facebook";
import { LinkCta } from "~/components/LinkCta";
import { Phone } from "~/components/icons/Phone";
import { Pin } from "~/components/icons/Pin";

import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";

type FooterLocalization = {
  followUs: string;
  privacyPolicy: string;
  termsConditions: string;
  accessibilityStatement: string;
  freeConsult: string;
  allRightsReserved: string;
};

const englishFooterLocalization: FooterLocalization = {
  followUs: "Follow us",
  privacyPolicy: "Privacy Policy",
  termsConditions: "Terms & Conditions",
  accessibilityStatement: "Accessibility Statement",
  freeConsult: "Free Consult",
  allRightsReserved: "All Rights Reserved.",
};

const spanishFooterLocalization: FooterLocalization = {
  followUs: "Síguenos",
  privacyPolicy: "Política de Privacidad",
  termsConditions: "Términos y Condiciones",
  accessibilityStatement: "Declaración de Accesibilidad",
  freeConsult: "Consulta Gratis",
  allRightsReserved: "Todos los Derechos Reservados.",
};

const localized: LocalizedStrings<FooterLocalization> = {
  [Locale.EN]: englishFooterLocalization,
  [Locale.ES]: spanishFooterLocalization,
};

export function Footer() {
  const getLocalization = useLocalization(localized);

  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <>
      <Flex width="100dvw" direction="column" pb={{ initial: "55px", sm: "0" }}>
        <Flex
          width="100%"
          justify="center"
          align="center"
          gap="2"
          className={styles.footerSocial}
        >
          <Text weight="bold" size="3">
            {getLocalization("followUs")}
          </Text>
          <a
            href="https://www.facebook.com/miamibraces/"
            target="_blank"
            rel="noreferrer"
            aria-label="Follow Miami Orthodontic Specialists on Facebook"
          >
            <Facebook size={16} />
          </a>
          <a
            href="https://www.instagram.com/miamibraces/"
            target="_blank"
            rel="noreferrer"
            aria-label="Follow Miami Orthodontic Specialists on Instagram"
          >
            <Instagram size={16} />
          </a>
        </Flex>

        <div className={styles.footerNav}>
          © {year} Miami Orthodontic Specialists.{" "}
          {getLocalization("allRightsReserved")} |{" "}
          <Link to="/privacy-policy">{getLocalization("privacyPolicy")}</Link>{" "}
          |{" "}
          <Link to="/terms-conditions">
            {getLocalization("termsConditions")}
          </Link>{" "}
          |{" "}
          <Link to="/accessibility-statement">
            {getLocalization("accessibilityStatement")}
          </Link>
        </div>
      </Flex>

      <Flex
        position="fixed"
        width="100dvw"
        height="55px"
        bottom="0"
        className={styles.mobileFooter}
        display={{ initial: "flex", sm: "none" }}
      >
        <Flex align="center" height="100%" flexBasis="50%">
          <LinkCta to="/free-consult" px="7">
            <Text size="3" weight="bold" style={{ textTransform: "none" }}>
              {getLocalization("freeConsult")}
            </Text>
          </LinkCta>
        </Flex>
        <Flex
          align="center"
          height="100%"
          flexBasis="25%"
          justify="center"
          className={`${styles.mobileFooterLink} ${styles.mobileFooterLocation}`}
          asChild
        >
          <Link to="/contact-us" aria-label="View our location">
            <Pin size={20} />
          </Link>
        </Flex>
        <Flex
          align="center"
          height="100%"
          flexBasis="25%"
          justify="center"
          className={styles.mobileFooterLink}
          asChild
        >
          <Link to="tel:305-407-9000" aria-label="Call Miami Orthodontic Specialists">
            <Phone size={20} />
          </Link>
        </Flex>
      </Flex>
    </>
  );
}
